import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { FLEX_CONTAINER, JUSTIFY_CONTENT, MARGIN, SHADOW } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, REF_ATTRIBUTE } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { PropertyConfig, PropertyOptions } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { RowUid } from "@cervval.js/common/src/ui/columnConfiguration/columnConfiguration";
import { CheckboxMode, CjsCheckbox } from "../checkbox/checkbox";
import { SELECTION_TYPE } from "./selectionModel/smartTableSelectionModel.types";
import { CjsSmartTable, RenderCacheData, SmartTableCell, SmartTableRow } from "./smartTable";
import { AbstractSmartTableColumn, SELECTOR_COLUMN_UID } from "./smartTableColumn.tools";

/** name of the cell attribute that stores the checkbox */
export const CELL_CHECKBOX = "_cellSelectorCheckbox";

export type SmartTableOnRowSelectEvent<ItemData> = {
    /** TableRow<ItemData> of selected Row */
    rowSelect: SmartTableRow<ItemData>,
    rowItemData: ItemData | null
}

export type SmartTableOnRowUnselectEvent<ItemData> = {
    /** TableRow<ItemData> of unselected Row */
    rowUnselect: SmartTableRow<ItemData>
}

export type SmartTableOnAllRowSelectEvent<ItemData> = {
    /** TableRow<ItemData> of rows that have been selected (only the new one) */
    rows: SmartTableRow<ItemData>[]
}

export type SmartTableOnAllRowUnselectEvent<ItemData> = {
    /** TableRow<ItemData> of rows that have been unselected (only already selected ones) */
    rows: SmartTableRow<ItemData>[]
}

/**
 * Column that displays checkbox representing the current selection
 * By default, when using SmartTableCheckboxSelectorColumn toggleSelectionOnClick is set to true (set on table options in Column constructor)
 */
export class SmartTableCheckboxSelectorColumn<ItemData = unknown> extends AbstractSmartTableColumn<ItemData> {

    /**
     * Event dispatched when row is select
     */
    @Event(NoBubblesEventInit)
    public rowSelectEvent!: EventHandler<SmartTableOnRowSelectEvent<ItemData>>;

    /**
     * Event dispatched when row is unselect
     */
    @Event(NoBubblesEventInit)
    public rowUnselectEvent!: EventHandler<SmartTableOnRowUnselectEvent<ItemData>>;

    /**
     * Event dispatched when all row are select
     */
    @Event(NoBubblesEventInit)
    public allRowsSelectEvent!: EventHandler<SmartTableOnAllRowSelectEvent<ItemData>>;

    /**
     * Event dispatched when all row are unselect
     */
    @Event(NoBubblesEventInit)
    public allRowsUnselectEvent!: EventHandler<SmartTableOnAllRowUnselectEvent<ItemData>>;

    /** The table where the column is inserted */
    public table: CjsSmartTable<ItemData>;

    /** Add selector column (radio or checkbox) (with select all checkbox in header) */
    public selectorMode: CheckboxMode;

    protected _headerCheckbox?: CjsCheckbox;
    protected _footerCheckbox?: CjsCheckbox;

    /** Selection in smartTable can only be performed with checkbox click */
    public rowSelectionWithCheckboxOnly = false;

    /** Store checkboxes by their row uid */
    public checkboxesByRow: Record<RowUid, CjsCheckbox> = {};

    /** Column property config */
    public override propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptions>;

    /** Getter for selectable rows cache */
    public get selectableRows(): SmartTableRow<ItemData>[] {

        // Initialize with selectable rows from the hierarchy (empty array if no hierarchy)
        const selectableRows: SmartTableRow<ItemData>[] = this.table.options.hierarchyOptions ? [...this.table.hierarchyHelper.selectableHierarchicalRows] : [];

        // Loop through displayable rows and push selectable rows
        for (const row of this.table.displayableRows) {
            if (!this.table.isSelectableRow(row)) { continue; }
            selectableRows.push(row);
        }

        return selectableRows;
    }

    constructor(table: CjsSmartTable<ItemData>, options?: {
        uid?: string,
        selectorMode?: CheckboxMode,
        filterEnabled?: boolean,
        sortEnabled?: boolean,
        rowSelectionWithCheckboxOnly?: boolean
    }) {
        super(options?.uid ?? SELECTOR_COLUMN_UID);
        this.table = table;

        if (options?.filterEnabled) {
            this.table.classList.add("with-selector-column-filterable");
        }

        // Update smart table selection options
        this.table.options.setWithoutRefresh({
            selection: {
                toggleSelectionOnClick: true,
                ...this.table.options.selection,
                selectionType: SELECTION_TYPE.MULTIPLE
            }
        });

        this.filterDisabled = !options?.filterEnabled;
        this.sortDisabled = !options?.sortEnabled;
        this.selectorMode = options?.selectorMode ?? CheckboxMode.CHECKBOX;
        this.rowSelectionWithCheckboxOnly = Boolean(options?.rowSelectionWithCheckboxOnly);
        this.title = (th) => this._renderHeaderComponent("_headerCheckbox", th);
        this.footer = (th) => this._renderHeaderComponent("_footerCheckbox", th);
        this.resizeDisabled = true;

        this.renderCell = this.renderCell.bind(this);

        // Update propertyConfig from uid
        this.propertyConfig = {
            label: "",
            propertyName: this.uid,
            typeUid: PropertyConfigType.CHECKBOX
        };
    }

    /**
     * Insert this column at first position
     */
    public insertFirst(): void {
        this.table.options.setWithoutRefresh({ columns: [this, ...this.table.options.columns] });
        if (this.table.options.columnsConfig) {
            this.table.options.setWithoutRefresh({
                columnsConfig: [
                    {
                        uid: this.uid,
                        order: 0,
                        visible: true,
                        width: this.width
                    },
                    ...this.table.options.columnsConfig
                ]
            });
        }
    }

    /**
     * @returns a "Select All" Checkbox if selectorMode === CheckboxMode.CHECKBOX
     */
    protected _renderHeaderComponent(checkBox: "_headerCheckbox" | "_footerCheckbox", thElement?: HTMLTableCellElement): AcceptedHtmlElementContent {


        // Adding header checkbox (no radio btn)
        if (this.selectorMode === CheckboxMode.CHECKBOX) {
            let checkbox: CjsCheckbox | undefined = this[checkBox];
            if (checkbox == null) {
                checkbox = new CjsCheckbox({
                    disabled: !Boolean(this.selectableRows.length)
                });
                checkbox.classList.add(MARGIN.MT_1, FLEX_CONTAINER, JUSTIFY_CONTENT.CENTER);
                checkbox.toggleEvent.on(_ => {
                    if (!checkbox) { return; }
                    this._onCheckboxHeaderChange(checkbox!);

                    // Update tooltip for header checkbox
                    if (checkBox === "_headerCheckbox") {
                        this._updateHeaderCheckboxTooltip(checkbox);
                    }
                });

                // Add a small elevation for select all checkbox, to differ from other checkboxes
                if (checkBox === "_headerCheckbox") {
                    checkbox.components.formCheckInput.classList.add(SHADOW.S1);
                    // Update tooltip for header checkbox
                    this._updateHeaderCheckboxTooltip(checkbox);
                }
                this[checkBox] = checkbox;
            }
            checkbox.setAttribute(REF_ATTRIBUTE, "checkboxHeaderInput");
            checkbox.setValue(Boolean(this.selectableRows.length) && !this.selectableRows.some(row => !row.selected), false);
            checkbox.options.setAndRefreshIfChanged({ disabled: !Boolean(this.selectableRows.length) });
            return checkbox;
        }
        return "";
    }

    /** Update checkbox title tooltip for header checkbox */
    private _updateHeaderCheckboxTooltip(checkbox: CjsCheckbox): void {
        checkbox.title = checkbox?.getValue() ? this.table.i18n.multiselect.unselectAll : this.table.i18n.multiselect.selectAll;
    }

    /**
     * @returns 1 if row is selected
     */
    public getCellValue(cell: SmartTableCell<ItemData>, renderCacheData?: RenderCacheData | undefined): number {
        return cell.row.selected ? 1 : 0;
    }

    /**
     * @returns a selection checkbox
     */
    public renderCell(cell: SmartTableCell<ItemData>, renderCacheData?: RenderCacheData): AcceptedHtmlElementContent | null {
        if (cell.row.sticky || !this.table.isSelectableRow(cell.row)) {
            return "";
        }
        const checkbox = cell[CELL_CHECKBOX as keyof SmartTableCell<ItemData>] as CjsCheckbox ?? new CjsCheckbox({ mode: this.selectorMode });
        checkbox.components.formCheckContainer.classList.add(JUSTIFY_CONTENT.CENTER);
        cell[CELL_CHECKBOX as keyof SmartTableCell<ItemData>] = checkbox;// keep checkbox in cell for future access
        this.checkboxesByRow[cell.row.uid] = checkbox;

        checkbox.setValue(cell.row.selected, cell.value === true);
        return checkbox;
    }

    /**
     * Update checkbox displays
     */
    public refreshSelectionDisplay(): void {
        // Clear checkboxes cache
        this.checkboxesByRow = {};

        for (const row of this.table.displayedRows) {
            const cell = this.table.getCellByRowAndColumn(row.uid, this.uid);
            if (cell?.[CELL_CHECKBOX as keyof SmartTableCell<ItemData>]) {
                this.renderCell(cell);
            }
        }
        if (this._headerCheckbox) {
            this._renderHeaderComponent("_headerCheckbox");
        }
        if (this._footerCheckbox) {
            this._renderHeaderComponent("_footerCheckbox");
        }
    }

    /**
     * Set header checkbox on
     */
    public selectAllRows(): void {
        this._headerCheckbox?.on();
    }

    /**
     * Callback triggered when click on checkbox header
     * Check/Uncheck all checkbox in table body following checkbox header checked property
     */
    protected _onCheckboxHeaderChange(checkboxHeader: CjsCheckbox): void {

        const previousSelectedRows = this.table.getSelectedRows();

        if (checkboxHeader.getValue()) {
            // Select all rows to select if selectable
            this.table.selectRows(this.selectableRows.map(r => r.uid), { preventScroll: true });

            this.allRowsSelectEvent.emit({
                rows: this.table.getSelectedRows().filter((r) => previousSelectedRows.indexOf(r) < 0) // Diff between getSelectedRows() and previousSelectedRows
            });
        } else {
            // Unselect all
            this.table.getSelectionModel().clear();

            this.allRowsUnselectEvent.emit({
                rows: previousSelectedRows // Only previousSelectedRows have been unselect
            });
        }
    }
}